/**
 * SigV4 presigned URL generator for AWS IoT Core WebSocket connections.
 *
 * Creates a `wss://` URL signed with temporary Cognito Identity Pool
 * credentials, allowing mqtt.js to connect from the browser.
 */

interface AwsCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken?: string;
}

// ── Crypto helpers (Web Crypto API) ─────────────────────────────────

async function hmacSha256(key: ArrayBuffer, data: string): Promise<ArrayBuffer> {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  return crypto.subtle.sign('HMAC', cryptoKey, new TextEncoder().encode(data));
}

async function sha256Hex(data: string): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(data));
  return bufToHex(hash);
}

function bufToHex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// ── SigV4 signing ───────────────────────────────────────────────────

async function deriveSigningKey(
  secretKey: string,
  date: string,
  region: string,
  service: string,
): Promise<ArrayBuffer> {
  const enc = new TextEncoder();
  let key: ArrayBuffer = enc.encode(`AWS4${secretKey}`).buffer as ArrayBuffer;
  for (const part of [date, region, service, 'aws4_request']) {
    key = await hmacSha256(key, part);
  }
  return key;
}

/**
 * Create a SigV4 presigned WSS URL for AWS IoT Core MQTT.
 *
 * @param endpoint  IoT ATS endpoint (e.g. "a1mogue2gh43jy-ats.iot.ap-southeast-2.amazonaws.com")
 * @param region    AWS region (e.g. "ap-southeast-2")
 * @param credentials  Temporary credentials from Cognito Identity Pool
 * @returns Signed `wss://` URL ready for mqtt.js
 */
export async function signIoTUrl(
  endpoint: string,
  region: string,
  credentials: AwsCredentials,
): Promise<string> {
  const now = new Date();
  const date = now.toISOString().slice(0, 10).replace(/-/g, ''); // YYYYMMDD
  const datetime = date + 'T' + now.toISOString().slice(11, 19).replace(/:/g, '') + 'Z'; // YYYYMMDDTHHMMSSZ
  const service = 'iotdevicegateway';
  const scope = `${date}/${region}/${service}/aws4_request`;

  // Canonical query string (alphabetical order)
  const queryParams = [
    `X-Amz-Algorithm=AWS4-HMAC-SHA256`,
    `X-Amz-Credential=${encodeURIComponent(`${credentials.accessKeyId}/${scope}`)}`,
    `X-Amz-Date=${datetime}`,
    `X-Amz-SignedHeaders=host`,
  ].join('&');

  // Canonical request
  const canonicalRequest = [
    'GET',
    '/mqtt',
    queryParams,
    `host:${endpoint}`,
    '',
    'host',
    'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855', // SHA-256 of empty string
  ].join('\n');

  // String to sign
  const stringToSign = [
    'AWS4-HMAC-SHA256',
    datetime,
    scope,
    await sha256Hex(canonicalRequest),
  ].join('\n');

  // Signature
  const signingKey = await deriveSigningKey(credentials.secretAccessKey, date, region, service);
  const signature = bufToHex(await hmacSha256(signingKey, stringToSign));

  // Build URL
  let url = `wss://${endpoint}/mqtt?${queryParams}&X-Amz-Signature=${signature}`;
  if (credentials.sessionToken) {
    url += `&X-Amz-Security-Token=${encodeURIComponent(credentials.sessionToken)}`;
  }

  return url;
}
